import type { Role } from '@jq4race/shared';
import {
  BarChart3,
  Building2,
  CalendarDays,
  ClipboardList,
  CreditCard,
  IdCard,
  LayoutDashboard,
  type LucideIcon,
  MapPin,
  MessageSquareWarning,
  MonitorSmartphone,
  QrCode,
  Settings,
  Ticket,
  Users,
} from 'lucide-react';

export interface NavItem {
  // Stable identifier — used as the React list key AND as the lookup suffix
  // for the translated label: t(`nav.items.${id}`) in the 'app-shell' namespace.
  // Display text intentionally does NOT live here; see locales/*/app-shell.json.
  id: string;
  icon: LucideIcon;
  to?: string;
  disabled?: boolean;
  // When set, the item is only rendered for users whose session role is in the
  // list. Multi-role gating (replaces the previous single-role exact-match) so an
  // item can be shared by, e.g., org_admin and supervisor. RBAC is always enforced
  // server-side and at the route's beforeLoad guard regardless of this hint.
  requiredRoles?: Role[];
}

export interface NavGroup {
  // Stable identifier — used as the React list key AND as the lookup suffix
  // for the translated eyebrow: t(`nav.groups.${id}`) in the 'app-shell' namespace.
  id: string;
  items: NavItem[];
  // When set, the group is only rendered for users whose session role is in the list.
  requiredRoles?: Role[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    items: [{ id: 'dashboard', icon: LayoutDashboard, to: '/dashboard' }],
  },
  {
    id: 'operate',
    items: [
      {
        id: 'supervisorDashboard',
        icon: MonitorSmartphone,
        to: '/dashboard/supervisor',
        requiredRoles: ['supervisor'],
      },
      {
        id: 'undeliveredSms',
        icon: MessageSquareWarning,
        to: '/dashboard/sms/undelivered',
        requiredRoles: ['org_admin', 'supervisor'],
      },
      {
        id: 'ticketAnalytics',
        icon: BarChart3,
        to: '/dashboard/analytics',
        requiredRoles: ['org_admin', 'supervisor'],
      },
      {
        id: 'tickets',
        icon: Ticket,
        to: '/dashboard/tickets',
        requiredRoles: ['org_admin', 'supervisor'],
      },
    ],
  },
  {
    id: 'manage',
    items: [
      { id: 'users', icon: Users, to: '/dashboard/users', requiredRoles: ['org_admin'] },
      {
        id: 'locations',
        icon: MapPin,
        to: '/dashboard/locations',
        requiredRoles: ['org_admin'],
      },
      {
        id: 'events',
        icon: CalendarDays,
        to: '/dashboard/events',
        requiredRoles: ['org_admin', 'supervisor'],
      },
      {
        id: 'settings',
        icon: Settings,
        to: '/dashboard/settings',
        requiredRoles: ['org_admin'],
      },
    ],
  },
  {
    id: 'memberships',
    requiredRoles: ['org_admin'],
    items: [
      {
        id: 'membershipPlans',
        icon: IdCard,
        to: '/dashboard/memberships/plans',
        requiredRoles: ['org_admin'],
      },
      {
        id: 'members',
        icon: Users,
        to: '/dashboard/memberships/members',
        requiredRoles: ['org_admin'],
      },
    ],
  },
  {
    id: 'audit',
    requiredRoles: ['org_admin'],
    items: [
      {
        id: 'auditLog',
        icon: ClipboardList,
        to: '/dashboard/audit-log',
        requiredRoles: ['org_admin'],
      },
    ],
  },
  {
    id: 'platform',
    requiredRoles: ['super_admin'],
    items: [
      {
        id: 'platformOverview',
        icon: LayoutDashboard,
        to: '/super-admin/overview',
      },
      {
        id: 'organizations',
        icon: Building2,
        to: '/super-admin/organizations',
      },
      {
        id: 'platformPlans',
        icon: CreditCard,
        to: '/super-admin/plans',
      },
      {
        id: 'qrGenerator',
        icon: QrCode,
        to: '/super-admin/qr-generator',
      },
      {
        id: 'team',
        icon: Users,
        to: '/super-admin/team',
        requiredRoles: ['super_admin'],
      },
      {
        id: 'platformAuditLog',
        icon: ClipboardList,
        to: '/super-admin/audit-log',
      },
    ],
  },
];

export interface RouteMeta {
  /** Breadcrumb path shown in the topbar. Last entry is the current page. */
  breadcrumb?: string[];
  /** Optional small uppercase label shown above the page title. */
  eyebrow?: string;
  /** Optional human title. Used as a fallback when a page does not render its own PageHeader. */
  title?: string;
}
